import React, { Dispatch, SetStateAction, SyntheticEvent, useState } from 'react';
import { Typography, Button, Modal, Grid, Switch, CardContent, Divider, CardActions } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { useIntl } from 'react-intl';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import useConfig from 'hooks/useConfig';
import { CookieProps } from 'types/config';

interface CookieOptionType {
  type: keyof CookieProps;
  labelId: string;
  descriptionId: string;
}

interface Props {
  cookieSettings: CookieProps;
  setCookieSettings: Dispatch<SetStateAction<CookieProps>>;
  open: boolean;
  handleCloseModal: (e: SyntheticEvent) => void;
}

const cookieOptions: CookieOptionType[] = [
  { type: 'necessary', labelId: 'COOKIE.SETTINGS.NECESSARY', descriptionId: 'COOKIE.SETTINGS.NECESSARY.SWITCH' },
  { type: 'performance', labelId: 'COOKIE.SETTINGS.PERFORMANCE', descriptionId: 'COOKIE.SETTINGS.PERFORMANCE.SWITCH' },
  { type: 'analytics', labelId: 'COOKIE.SETTINGS.ANALYTICS', descriptionId: 'COOKIE.SETTINGS.ANALYTICS.SWITCH' },
  { type: 'advertisement', labelId: 'COOKIE.SETTINGS.ADVERTISEMENT', descriptionId: 'COOKIE.SETTINGS.ADVERTISEMENT.SWITCH' },
  { type: 'others', labelId: 'COOKIE.SETTINGS.OTHERS', descriptionId: 'COOKIE.SETTINGS.OTHERS.SWITCH' }
];

const CookieSettingsModal: React.FC<Props> = ({ open, handleCloseModal, cookieSettings, setCookieSettings }: Props) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { onChangeCookiePolicy } = useConfig();
  const [showFullText, setShowFullText] = useState(false);
  const [openDetails, setOpenDetails] = useState<string | null>(null);

  const saveCookie = () => {
    onChangeCookiePolicy(cookieSettings);
    handleCloseModal;
  };

  const handleSwitchChange = (type: keyof Omit<CookieProps, 'necessary'>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setCookieSettings((prevSettings) => ({
      ...prevSettings,
      [type]: event.target.checked
    }));
  };

  const cookieText = formatMessage({ id: 'COOKIE.SETTINGS.TEXT' });
  const truncatedText = cookieText.length > 229 ? `${cookieText.substring(0, 229)}...` : cookieText;

  const handleIconClick = (type: string) => {
    setOpenDetails(openDetails === type ? null : type);
  };

  const CookieOption: React.FC<CookieOptionType> = ({ type, labelId, descriptionId }) => {
    return (
      <Grid
        size={{ xs: 12 }}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
          padding: 1,
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 1
        }}
      >
        <Grid container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Grid size={{ xs: 9 }} onClick={() => handleIconClick(type)} sx={{ cursor: 'pointer' }}>
            <Grid container>
              <Grid size={{ xs: 2, lg: 1 }}>
                {openDetails === type ? (
                  <IconChevronDown size={16} color="grey" style={{ cursor: 'pointer' }} />
                ) : (
                  <IconChevronRight size={16} color="grey" style={{ cursor: 'pointer' }} />
                )}
              </Grid>
              <Grid size={{ xs: 10, lg: 11 }}>
                <Typography textAlign="start">{formatMessage({ id: labelId })}</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 3 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Switch
              checked={cookieSettings[type]}
              onChange={type !== 'necessary' ? handleSwitchChange(type) : undefined}
              disabled={type === 'necessary'}
            />
          </Grid>

          <Grid size={{ xs: 12 }} onClick={() => handleIconClick(type)} sx={{ cursor: 'pointer' }}>
            {openDetails === type && (
              <Typography
                sx={{
                  padding: 1,
                  fontSize: 13,
                  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[600],
                  textAlign: 'left'
                }}
              >
                {formatMessage({ id: descriptionId })}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Modal open={open} onClose={handleCloseModal} disableScrollLock disableEnforceFocus>
      <MainCard
        sx={{
          position: 'absolute',
          width: { xs: 320, sm: 450, md: 550 },
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}
        title={formatMessage({ id: 'COOKIE.SETTINGS.TITLE' })}
        content={false}
      >
        <CardContent sx={{ overflowY: 'auto', maxHeight: { xs: '500px', lg: '600px' } }}>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <Typography lineHeight={1.4}>{showFullText ? cookieText : truncatedText}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }} mb={2} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              {cookieText.length > 229 && (
                <Button variant="text" onClick={() => setShowFullText(!showFullText)}>
                  {showFullText ? formatMessage({ id: 'COOKIE.SETTINGS.SHOW.LESS' }) : formatMessage({ id: 'COOKIE.SETTINGS.SEE.MORE' })}
                </Button>
              )}
            </Grid>
            {cookieOptions.map(({ type, labelId, descriptionId }, index) => (
              <CookieOption key={index} type={type} labelId={labelId} descriptionId={descriptionId} />
            ))}
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outlined" color="primary" size="medium" onClick={handleCloseModal}>
            {formatMessage({ id: 'COOKIE.SETTINGS.BACK.BUTTON' })}
          </Button>
          <AnimateButton>
            <Button variant="contained" color="primary" size="medium" onClick={saveCookie}>
              {formatMessage({ id: 'COOKIE.SETTINGS.SAVE.BUTTON' })}
            </Button>
          </AnimateButton>
        </CardActions>
      </MainCard>
    </Modal>
  );
};

export default CookieSettingsModal;
